import { useState, useEffect } from "react";
import { Sparkles, X, RefreshCw } from "lucide-react";

const DEFAULT_SYSTEM_INSTRUCTIONS = `# Task
Please analyze the above data and suggest optimal table assignments that:
- Maximize compatibility between diners based on their quiz answers
- Respect table capacity constraints
- Consider any special requests from diners
- If a user requested a sapphic table, try to assign them to a table with other sapphic users (preferably also those that requested a sapphic table, but not necessarily).
- Try to avoid assigning users to the same table if they have already been tablemates in the past.
- Try to avoid assigning users to restaurants they have been to in the past.
- Consider restaurant tags when making assignments (e.g., vegetarians might prefer restaurants with has_vegetarian_options tag, budget-conscious users might prefer budget_friendly restaurants, etc.)
- Tables are locked if they are already assigned to a group of users and should not be assigned to any other users.
- Try to not re-assign users that are already assigned to a table.
- Do only return new assignments, ignore users that should not be reassigned.
- Try to fill all tables to their maximum capacity rather than creating tables with few users. It is okay to re-assign users if it means ensuring all users have a good amount of tablemates.`;

interface AutoMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (systemInstructions: string) => void;
  isLoading: boolean;
}

export function AutoMatchModal({
  isOpen,
  onClose,
  onGenerate,
  isLoading,
}: AutoMatchModalProps) {
  const [instructions, setInstructions] = useState(DEFAULT_SYSTEM_INSTRUCTIONS);

  // Reset instructions when modal opens
  useEffect(() => {
    if (isOpen) {
      setInstructions(DEFAULT_SYSTEM_INSTRUCTIONS);
    }
  }, [isOpen]);

  const handleGenerate = () => {
    onGenerate(instructions);
  };

  const handleReset = () => {
    setInstructions(DEFAULT_SYSTEM_INSTRUCTIONS);
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-900">
                  Auto-Generate Matches
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
                disabled={isLoading}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(85vh-12rem)]">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  System Instructions
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Customize the instructions for the AI matching algorithm. The default
                  instructions are designed to create balanced, compatible tables.
                </p>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                  placeholder="Enter system instructions..."
                  disabled={isLoading}
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleReset}
                  className="btn btn-ghost btn-sm"
                  disabled={isLoading}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset to Default
                </button>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="btn btn-ghost"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                className="btn btn-primary"
                disabled={isLoading || !instructions.trim()}
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Matches
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export { DEFAULT_SYSTEM_INSTRUCTIONS };

